/**
 * Single-flight scheduler with one trailing run.
 *
 * Rules:
 * - At most one task runs at a time.
 * - If `schedule()` is called while a task is running, the new task is stored
 *   as "pending" and replaces any previously pending task (multiple calls
 *   during a single save collapse into exactly one trailing run).
 * - The trailing run fires after the in-flight run settles — whether it
 *   succeeded or failed.
 * - The trailing task is the callback passed to the most-recent `schedule()`
 *   call, so callers can supply a fresh closure (e.g. from a ref updated
 *   every render) to ensure the trailing run reads current state.
 *
 * Usage in useFlowPersistence:
 *   // Lazy init so makeTrailingSave() is only called once, not on every render.
 *   const schedulerRef = useRef<TrailingSaveScheduler | null>(null);
 *   if (schedulerRef.current === null) schedulerRef.current = makeTrailingSave();
 *   schedulerRef.current.schedule(saveFlowBody);
 */
export function makeTrailingSave() {
  let running = false;
  let pendingTask: (() => Promise<void>) | null = null;
  let idleListeners: Array<() => void> = [];

  function notifyIdle(): void {
    if (running || pendingTask !== null) return;
    const listeners = idleListeners;
    idleListeners = [];
    for (const l of listeners) l();
  }

  async function execute(task: () => Promise<void>): Promise<void> {
    running = true;
    try {
      await task();
    } finally {
      running = false;
      if (pendingTask !== null) {
        const next = pendingTask;
        pendingTask = null;
        // Fire trailing run asynchronously so any throw from `task` does not
        // prevent the trailing run, and so errors inside either run are handled
        // by the task itself (saveFlow has its own try/catch).
        void execute(next).catch(() => {});
      } else {
        // No queued work: scheduler is now truly idle.
        notifyIdle();
      }
    }
  }

  return {
    /** Schedule a task.  Runs immediately when idle; otherwise queues one
     *  trailing run that fires after the current task settles. */
    schedule(task: () => Promise<void>): void {
      if (running) {
        pendingTask = task; // collapse: keep only the latest request
        return;
      }
      void execute(task).catch(() => {});
    },

    /**
     * Returns a Promise that resolves once the scheduler is idle (no running
     * task and no pending task).  Resolves immediately if already idle.
     *
     * Use in createNewVersion to ensure no trailing save will PUT to the old
     * version after the new one is created.
     */
    idle(): Promise<void> {
      if (!running && pendingTask === null) return Promise.resolve();
      return new Promise<void>((resolve) => {
        idleListeners.push(resolve);
      });
    },

    /** True while a task is executing. */
    get isRunning(): boolean {
      return running;
    },

    /** True when a trailing run is queued but has not yet started. */
    get hasPending(): boolean {
      return pendingTask !== null;
    },
  };
}

export type TrailingSaveScheduler = ReturnType<typeof makeTrailingSave>;
